import Broadcast from "@/types/Broadcast.interface";
import Campaign from "@/types/Campaign.interface";
import Channel from "@/types/Channel.interface";
import Invoice from "@/types/Invoice.interface";
import filters from '@/utility/filters'
import moment from 'moment-timezone'
import message from '@/types/Message.interface'
import Message from "@/types/Message.interface";
import chatbots from "@/store/data/chatbots";
import formSubmissions from "@/store/data/formSubmissions";
import Media from "@/types/Media.interface";
export interface FilterObject {
    //  The key on our object we compare AND use to find our function
    key: string
    //  An object of functions that we trigger based on the val of our params from params[key]
    functions: object
    //  A default function we use when we have to deal with id based filtering
    default?: Function
}

export default {
    autoresponders: [
        {
            key: 'type',
            functions: {
                all: () => true,
                active: (x: Campaign) => !x.paused,
                paused: (x: Campaign) => x.paused != null
            }
        }
    ] as FilterObject[],
    broadcasts: [
        {
            key: 'group_id',
            functions: {},
            default: (x: Broadcast, val: number | null) => !val || x.group_id === val
        },
        {
            key: 'type',
            functions: {
                all: () => true,
                completed: (x: Broadcast) => x.status === 'completed',
                draft: (x: Broadcast) => x.status === 'draft',
                scheduled: (x: Broadcast) => x.status === 'scheduled',
                sending: (x: Broadcast) => x.status === 'sending'
            }
        }
    ] as FilterObject[],
    inbox: [
        {
            key: 'assigned',
            default: (x: Channel, y: number) => x.assigned_id === y,
            functions: {
                everyone: () => true,
                unassigned: (x: Channel) => !x.assigned_id
            }
        },
        {
            key: 'status',
            default: () => true,
            functions: {
                new: (x: Channel) => filters.channelStatus(x) === 'NEW',
                replied: (x: Channel) => filters.channelStatus(x) === 'Replied',
                waiting: (x: Channel) => filters.channelStatus(x) === 'Waiting',
            }
        },
        {
            key: 'type',
            functions: {
                open: (x: Channel) => x.open !== null && !x.closed,
                closed: (x: Channel) => x.closed !== null
            }
        }
    ] as FilterObject[],
    mediaView: [
        {
            key: 'filter',
            default: () => true, 
            functions: {
                favorite: (x: Media) => x.favorite,
                images: (x: Media) => x.type.includes('image'),
                index: (x: Media) => !x.folder,
                messages: (x: Media) => x.type.includes('image') && (x.folder === 'uploaded' || !x.folder),
                pdfs: (x: Media) => x.type === 'application/pdf'
            }
        }
    ],
    messagesTable: [
        {
            // Key associates with key in the pagination request in MessagesTableController.php
            key: 'outboundMessages',
            // Default is the case we run through if no values match what's in the functions, in this case if no filter is selected
            default: () => true, 
            functions: {
                // These function values are unique and must be called true and false since api expects a boolean 
                true: (x: Message) => x.outbound,
                false: (x: Message) => !x.outbound
            }
        },
        {
            key: 'deliveredMessages',
            functions: {
                // These functions values match with the values in MessagesTableController.php validatePagination
                // it's what the api expects us to pass whenever we have the key called deliveredMessages (key/value pair)
                delivered: (x: Message) => x.delivered !== null,
                'not delivered': (x: Message) => x.delivered === null,
                ascending: () => true,
                descending: () => true
            }
        },
        {
            key: 'messagesType',
            // In this case we always check messagesType and either include all messages or exclude those whose type doesn't match our type param
            default: (x: Message, type: string) => !type || x.type === type,
            // Even if their is no direct logic needed for a value we still must include the functions key to prevent errors 
            functions: {}
        },
        {
            key: 'status',
            // X is the mesage we are comparing to the status that is in our messageTableParams state, any function with two params is doing this
            default: (x: Message, status: string) => !status || x.status === status,
            functions: {}
        },
        {
            key: 'startDate',
            default: (x: Message, startDate: Date) => x.created_at >= startDate,
            functions: {}
        },
        {
            key: 'endDate',
            default: (x: Message, endDate: Date) => x.created_at <= endDate,
            functions: {}
        },
        {
            key: 'search',
            // Includes functions a lot like the %LIKE% query in SQL returning messages that have most of or all the characters 
            // present in the users search bar. We use toLowerCase otherwise messages with different capitilizations would be filtered out
            default: (x: Message, search: string) => !search || (x.text && x.text.toLowerCase().includes(search.toLowerCase())),
            functions: {}
        }
    ] as FilterObject[],
    payments: [
        {
            key: 'filter',
            functions: {
                asc: () => true,
                desc: () => true,
                highest_bal: (x: Invoice) => x.balance != 0,
                lowest_bal: (x: Invoice) => x.balance != 0,
                not_sent: (x: Invoice) => !x.sent_at,
                open: (x: Invoice) => x.balance != 0,
                overdue: (x: Invoice) => x.balance != 0 && x.due_date <= moment.utc().toDate(),
                paid: (x: Invoice) => x.balance == 0,
                sent: (x: Invoice) => x.sent_at != null,
                upcoming: (x: Invoice) => x.balance != 0 && x.due_date > moment.utc().toDate()
            }
        }
    ] as FilterObject[]
}